//@flow
import React from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import { FormattedMessage, FormattedPlural, FormattedNumber } from 'react-intl';
import getter from 'dibs-object-getter';
import { Button } from 'dibs-elements/src/Button';
import EnvelopeIcon from 'dibs-icons/exports/Envelope';

import styles from './FirmDetails.scss';

import type { FirmDetails_photoQuery as TradePhotoQueryOutput } from './__generated__/FirmDetails_photoQuery.graphql';

type FirmDetailsProps = {
    photoQuery: ?TradePhotoQueryOutput,
};

export function FirmDetailsComponent({
    firmName,
    firmProfileImage,
    cityStateCountry,
    profileLink,
    designProjectsCount,
    isLoadingProjects,
}: {
    firmName: ?string,
    firmProfileImage: string,
    cityStateCountry: ?string,
    profileLink: string,
    designProjectsCount: ?number,
    isLoadingProjects?: boolean,
}) {
    const backgroundStyle = {
        background: `url(${firmProfileImage}?width=100)`,
    };
    return (
        <div className={styles.wrapper}>
            <div className={styles.content}>
                <a className={styles.profileIconLink} href={profileLink} style={backgroundStyle} />
                <div>
                    <a className={styles.firmName} href={profileLink}>
                        {firmName}
                    </a>
                    <div className={styles.address}>{cityStateCountry}</div>
                </div>
            </div>
            {
                <div
                    className={`${styles.projectsWrapper} ${
                        isLoadingProjects ? styles.isLoading : ''
                    }`.trim()}
                >
                    {!!designProjectsCount && (
                        <React.Fragment>
                            <span className={styles.projectCount}>
                                <FormattedNumber value={designProjectsCount} />
                            </span>{' '}
                            <span className={styles.projectsLabel}>
                                <FormattedMessage
                                    id="interiorPhoto.rail.firmDetails.projectsOn1stdibs"
                                    defaultMessage="{project} on 1stdibs"
                                    values={{
                                        project: (
                                            <FormattedPlural
                                                value={designProjectsCount}
                                                one={
                                                    <FormattedMessage
                                                        id="interiorPhoto.rail.firmDetails.project"
                                                        defaultMessage="Project"
                                                    />
                                                }
                                                other={
                                                    <FormattedMessage
                                                        id="interiorPhoto.rail.firmDetails.projects"
                                                        defaultMessage="Projects"
                                                    />
                                                }
                                            />
                                        ),
                                    }}
                                />
                            </span>
                        </React.Fragment>
                    )}
                </div>
            }
            <div className={styles.infoWrapper}>
                <Button
                    href={profileLink}
                    dataTn="view-profile-button"
                    type="secondary"
                    size="small"
                >
                    <FormattedMessage
                        id="interiorPhoto.rail.firmDetails.viewProfile"
                        defaultMessage="View Profile"
                    />
                </Button>
            </div>
            <a href={`${profileLink}?modal=contactFirm`} className={styles.contactCta}>
                <EnvelopeIcon className={styles.contactIcon} />
                <FormattedMessage
                    id="interiorPhoto.rail.firmDetails.contact"
                    defaultMessage="Contact"
                />
            </a>
        </div>
    );
}

export const FirmDetails = (props: FirmDetailsProps) => {
    const photoQueryGetter = getter(props).at('photoQuery');
    const firmName = photoQueryGetter.value('firmName');
    const firmPublicProfileGetter = photoQueryGetter.at('firmInflatedDetails').at('publicProfile');
    const cityStateCountry = firmPublicProfileGetter.at('address').value('displayCityStateCountry');
    const designProjectsCount = firmPublicProfileGetter.value('designProjectsCount');
    const firmProfileImage = photoQueryGetter.value('firmProfileIcon') || '';
    const firmUrlLabel = photoQueryGetter.value('firmUrlLabel') || '';
    const profileLink = `/design-firms/${firmUrlLabel}/`;

    return (
        <FirmDetailsComponent
            firmName={firmName}
            firmProfileImage={firmProfileImage}
            cityStateCountry={cityStateCountry}
            profileLink={profileLink}
            designProjectsCount={designProjectsCount}
        />
    );
};

export default createFragmentContainer(FirmDetails, {
    photoQuery: graphql`
        fragment FirmDetails_photoQuery on TradePhotosQueryOutput {
            firmName
            firmUrlLabel
            firmProfileIcon
            firmInflatedDetails {
                publicProfile {
                    address {
                        displayCityStateCountry
                    }
                    designProjectsCount
                }
            }
        }
    `,
});
